import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../api/settings.api';
import type { UpdateSettingsRequest } from '../types/settings.types';

/**
 * Hook to manage user settings
 * Uses React Query for data fetching and mutations
 */
export const useSettings = () => {
  const queryClient = useQueryClient();

  // Fetch settings
  const {
    data: settingsResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['userSettings'],
    queryFn: settingsApi.fetchSettings,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in React Query v5)
  });

  const settings = settingsResponse?.data?.settings;

  // Update settings mutation
  const {
    mutate: updateSettings,
    mutateAsync: updateSettingsAsync,
    isPending: isUpdating,
    error: updateError
  } = useMutation({
    mutationFn: (newSettings: UpdateSettingsRequest) =>
      settingsApi.updateSettings(newSettings),
    onSuccess: (response) => {
      // Update cache with new settings
      queryClient.setQueryData(['userSettings'], response);
      console.log('[useSettings] Settings updated successfully');
    },
    onError: (error) => {
      console.error('[useSettings] Failed to update settings:', error);
    }
  });

  return {
    settings,
    isLoading,
    isUpdating,
    error,
    updateError,
    updateSettings,
    updateSettingsAsync,
    refetch
  };
};
